// @flow
import React, { Component } from 'react';
import { injectIntl, type IntlShape } from 'react-intl';

import DialogPanel from 'portfolio-shared/view/DialogPanel/DialogPanel';
import { hideDialogPanel } from 'state/commands/dialogPanelCommands';
import { BACKUP_RESTORE_PROGRESS_PANEL_ID } from 'util/constants';
import { type BackupRestoreStatus } from 'state/types';
import localizedStrings from 'util/localizedStrings';
import {
  BACKUP_OPERATION_STATE,
  BACKUP_RESTORE_FAULT_CODE,
} from 'util/AdminProperties';
import {
  bytesToString,
  isBackupRestoreBusy,
  isBackupRestoreCancelled,
  isBackupRestoreFailed,
  isBackupOperation,
  isBackupRestoreSuccessful,
  isRestoring,
} from 'util/backupUtils';
import { cancelBackup } from 'state/commands/adminPropertiesCommands';
import logger from 'portfolio-shared/util/logging';

type Props = {
  activePanelId: ?string,
  backupRestoreStatus: ?BackupRestoreStatus,
  cancelBackup: typeof cancelBackup,
  hideDialogPanel: typeof hideDialogPanel,
  intl: IntlShape,
};

type State = {
  isCancelling: boolean,
};

class BackupRestoreProgressDialog extends Component<Props, State> {
  handleCloseButton: Function;
  isOkEnabled: Function;

  state = { isCancelling: false };

  constructor() {
    super();

    this.handleCloseButton = this.handleCloseButton.bind(this);
    this.isOkEnabled = this.isOkEnabled.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.backupRestoreStatus !== this.props.backupRestoreStatus) {
      if (isBackupRestoreCancelled(this.props.backupRestoreStatus)) {
        this.setState({ isCancelling: false });
      }
    }
  }

  getButtonText() {
    const {
      backupRestoreStatus,
      intl: { formatMessage },
    } = this.props;

    return isBackupRestoreBusy(backupRestoreStatus)
      ? formatMessage(localizedStrings.CANCEL)
      : formatMessage(localizedStrings.OK);
  }

  getDialogTitle() {
    const {
      backupRestoreStatus,
      intl: { formatMessage },
    } = this.props;

    if (backupRestoreStatus) {
      switch (backupRestoreStatus.currentOperationState) {
        case BACKUP_OPERATION_STATE.BACKUP_PREFLIGHT:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_PREFLIGHT);

        case BACKUP_OPERATION_STATE.BACKUP_SHUTTING_DOWN_SERVER:
        case BACKUP_OPERATION_STATE.BACKUP_BACKING_UP_DATA:
        case BACKUP_OPERATION_STATE.BACKUP_RESTARTING_SERVER:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_BACKING_UP);

        case BACKUP_OPERATION_STATE.BACKUP_COMPLETE:
          return formatMessage(
            localizedStrings.BACKUP_PROGRESS_BACKUP_SUCCESSFUL
          );

        case BACKUP_OPERATION_STATE.BACKUP_FAILED:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_BACKUP_FAILED);

        case BACKUP_OPERATION_STATE.RESTORE_SHUTTING_DOWN_SERVER:
        case BACKUP_OPERATION_STATE.RESTORE_RESTORE_DATA:
        case BACKUP_OPERATION_STATE.RESTORE_RESTARTING_SERVER:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_RESTORING);

        case BACKUP_OPERATION_STATE.RESTORE_COMPLETE:
          return formatMessage(
            localizedStrings.BACKUP_PROGRESS_RESTORE_SUCCESSFUL
          );

        case BACKUP_OPERATION_STATE.RESTORE_FAILED:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_RESTORE_FAILED);

        default:
          return `Unknown Backup Restore Status: ${
            backupRestoreStatus.currentOperationState
          }`;
      }
    }
  }

  getDetailString() {
    const {
      backupRestoreStatus,
      intl: { formatMessage },
    } = this.props;

    if (backupRestoreStatus) {
      switch (backupRestoreStatus.currentOperationState) {
        case BACKUP_OPERATION_STATE.BACKUP_PREFLIGHT:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_PREFLIGHT);

        case BACKUP_OPERATION_STATE.BACKUP_SHUTTING_DOWN_SERVER:
        case BACKUP_OPERATION_STATE.RESTORE_SHUTTING_DOWN_SERVER:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_SHUTTING_DOWN);

        case BACKUP_OPERATION_STATE.BACKUP_BACKING_UP_DATA:
          return formatMessage(
            localizedStrings.BACKUP_PROGRESS_BACKING_UP_DATA
          );

        case BACKUP_OPERATION_STATE.RESTORE_RESTORE_DATA:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_RESTORING_DATA);

        case BACKUP_OPERATION_STATE.BACKUP_RESTARTING_SERVER:
        case BACKUP_OPERATION_STATE.RESTORE_RESTARTING_SERVER:
          return formatMessage(localizedStrings.BACKUP_PROGRESS_RESTARTING);

        default:
          return undefined;
      }
    }
  }

  getProgressValue() {
    const { backupRestoreStatus } = this.props;

    if (backupRestoreStatus) {
      switch (backupRestoreStatus.currentOperationState) {
        case BACKUP_OPERATION_STATE.BACKUP_PREFLIGHT:
          return 1;

        case BACKUP_OPERATION_STATE.BACKUP_SHUTTING_DOWN_SERVER:
        case BACKUP_OPERATION_STATE.RESTORE_SHUTTING_DOWN_SERVER:
          return 2;

        case BACKUP_OPERATION_STATE.BACKUP_BACKING_UP_DATA:
        case BACKUP_OPERATION_STATE.RESTORE_RESTORE_DATA:
          return 3;

        case BACKUP_OPERATION_STATE.BACKUP_RESTARTING_SERVER:
        case BACKUP_OPERATION_STATE.RESTORE_RESTARTING_SERVER:
          return 4;

        default:
          return 0;
      }
    }
  }

  getErrorDetail() {
    const {
      backupRestoreStatus,
      intl: { formatMessage },
    } = this.props;

    if (backupRestoreStatus) {
      switch (backupRestoreStatus.faultCode) {
        case BACKUP_RESTORE_FAULT_CODE.INTERNAL_ERROR:
        case BACKUP_RESTORE_FAULT_CODE.LIFECYCLE_ERROR:
          return isBackupOperation(backupRestoreStatus)
            ? formatMessage(
                localizedStrings.BACKUP_RESTORE_ERROR_MSG_UNRECOVERABLE_BACKUP
              )
            : formatMessage(
                localizedStrings.BACKUP_RESTORE_ERROR_MSG_UNRECOVERABLE_RESTORE
              );
        case BACKUP_RESTORE_FAULT_CODE.BAD_ARCHIVE:
          return formatMessage(
            localizedStrings.BACKUP_RESTORE_ERROR_MSG_BAD_ARCHIVE
          );
        case BACKUP_RESTORE_FAULT_CODE.INSUFFICIENT_SPACE:
          return formatMessage(
            localizedStrings.BACKUP_RESTORE_ERROR_MSG_INSUFFICIENT_SPACE,
            {
              requiredSpace: bytesToString(backupRestoreStatus.spaceRequired),
              availableSpace: bytesToString(backupRestoreStatus.spaceAvailable),
            }
          );
        default:
          logger.error(
            `Unknown Backup Restore Fault Code: ${backupRestoreStatus.faultCode ||
              '<undefined>'}`
          );
      }
    }
  }

  renderProgress() {
    const { backupRestoreStatus } = this.props;

    if (isBackupRestoreBusy(backupRestoreStatus)) {
      if (this.getProgressValue()) {
        return (
          <progress
            className="progress is-primary"
            data-test-name="backup-restore-current-progress"
            value={this.getProgressValue()}
            max="4"
            style={{ width: '50%' }}
          />
        );
      }
    } else if (isBackupRestoreSuccessful(backupRestoreStatus)) {
      return <i className="fa fa-check fa-5x fa-fw has-text-primary" />;
    } else if (isBackupRestoreFailed(backupRestoreStatus)) {
      return this.getErrorDetail();
    }
  }

  handleCloseButton() {
    const { backupRestoreStatus, cancelBackup, hideDialogPanel } = this.props;

    if (isBackupRestoreBusy(backupRestoreStatus)) {
      cancelBackup();
      this.setState({ isCancelling: true });
    } else {
      this.setState({ isCancelling: false });
      hideDialogPanel();
    }
  }

  isOkEnabled() {
    return (
      !this.state.isCancelling && !isRestoring(this.props.backupRestoreStatus)
    );
  }

  render() {
    const { activePanelId, backupRestoreStatus, hideDialogPanel } = this.props;

    return (
      <DialogPanel
        activePanelId={activePanelId}
        closeAfterSave={false}
        contentStyle={{ width: 600, height: 375 }}
        customButtonText={this.getButtonText()}
        dataTestName="backup-restore-progress-dialog"
        hideDialogPanel={hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onSave={this.handleCloseButton}
        panelId={BACKUP_RESTORE_PROGRESS_PANEL_ID}
        panelTitle={this.getDialogTitle()}
        showCancel={false}
      >
        <div className="container" style={{ paddingTop: '25px' }}>
          <div className="level">
            <div className="level-item">
              {isBackupRestoreBusy(backupRestoreStatus) && (
                <i className="fa fa-circle-o-notch fa-spin fa-3x fa-fw has-text-primary" />
              )}
            </div>
          </div>
          <div className="level">
            <div
              className="level-item"
              data-test-name="backup-restore-detail-string"
            >
              {this.getDetailString()}
            </div>
          </div>
          <div className="level">
            <div className="level-item">{this.renderProgress()}</div>
          </div>
        </div>
      </DialogPanel>
    );
  }
}

export default injectIntl(BackupRestoreProgressDialog);



// WEBPACK FOOTER //
// ./src/view/App/DialogPanels/BackupRestoreProgressDialog.js